import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  addDoc,
  collection,
  serverTimestamp,
} from '@angular/fire/firestore';
import { AuthStore } from '@core/services/auth/auth.store';
import { WorkspaceStore } from '@shared/services/workspace.store';

/**
 * Fire-and-forget product-analytics emitter (frontend).
 *
 * Writes one `analytics_activity` doc with `type: "product"` per tracked user
 * action, so the admin Usage surfaces can answer "what do users actually do".
 *
 * Contract (mirrors the backend `logProductEvent` helper):
 *  - NEVER throws, NEVER blocks the user action — the write is fired and its
 *    promise is swallowed. Callers must not await `track()`.
 *  - No-op when unauthenticated or without a current workspace.
 *  - `createdAt` MUST be `serverTimestamp()` — the create-only firestore rule
 *    requires `createdAt == request.time`.
 *  - Payloads stay small (ids + enums). Never PII.
 *
 * Counters are derived server-side in the daily engagement sweep; these events
 * exist for timeline/recency/attribution only.
 */
@Injectable({ providedIn: 'root' })
export class ProductAnalyticsService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);

  track(event: string, payload?: Record<string, unknown>): void {
    const uid = this.authStore.user()?.userUID;
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!uid || !workspaceId) return;

    void addDoc(collection(this.firestore, 'analytics_activity'), {
      type: 'product',
      source: 'frontend',
      event,
      userId: uid,
      workspaceId,
      ...(payload ? { payload } : {}),
      createdAt: serverTimestamp(),
    }).catch(() => {
      /* analytics is best-effort — never surface an error to the user */
    });
  }
}
